import RequestStatus from 'todistukset-ui/src/vtj/RequestStatus.enum'
import fp from 'lodash/fp'
import { toJS } from 'mobx'
import { createActionWithRefreshSession } from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import { createFlow, createAction } from 'common/src/vtj/mobx/action-creator'
import { handleError } from 'todistukset-ui/src/vtj/stores/actions/common-actions'
import { post, Result as HttpResult } from 'common/src/vtj/browser/http'
import {
  MAX_PREVIOUS_ADDRESSES_OVER_YEARS,
  removeSpaces,
} from 'todistukset-common/src/vtj/form-validation.util'
import {
  normalizeTextInputWhitespace,
  sanitizeMailDeliveryInput,
} from 'todistukset-common/src/vtj/form-sanitization.util'
import { clearVisitsAfterCurrentStep } from 'todistukset-ui/src/vtj/stores/actions/steps-actions'
import { checkIfTranslationIsAvailable } from 'todistukset-ui/src/vtj/translation.util'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { isCountryInEUByCode } from 'todistukset-common/src/vtj/country.util'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import CertificateSubtypes from 'todistukset-common/src/vtj/CertificateSubtypes.enum'
import { isValueInEnum } from 'todistukset-common/src/vtj/type-guards'
import { UsageRegionTarget } from 'todistukset-common/src/vtj/UsageRegionTarget.enum'
import CertificateTypes, {
  VtjOteTodistusType,
} from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import {
  MailDelivery,
  OrdererExtraIdentification,
} from 'todistukset-common/src/vtj/types/todistus'
import {
  huollettavaToPerson,
  isEidasOrderer,
  isSameHetuOrEidasPID,
} from 'todistukset-common/src/vtj/user.utils'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'
import { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import {
  EditableField,
  editIlmoitettuArvo,
  emptyEditableField,
  finalEditableFieldValue,
  IncludeApostille,
  IncludeKaannos,
  IncludePreviousKotikunta,
  IncludePreviousPermanentAddresses,
  IncludePreviousTemporaryAddresses,
  KaannosKieli,
  mapEditableField,
  PreviousAddressesOverYears,
  TodistusKieli,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import { TextInputValue } from 'suomifi-ui-components'
import { TodistusPersonRole } from 'todistukset-common/src/vtj/types/todistus-person.type'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import type { Huollettava } from 'todistukset-common/src/vtj/vtj-kysely/huoltod-soap-response-converter'
import { langIsTodistusasiointikieli } from 'todistukset-common/src/vtj/language.util'

type Wrapped<Fn> = Fn extends (
  todistusType: CertificateTypes,
  ...rest: infer Args
) => unknown
  ? (...args: Args) => ReturnType<Fn>
  : never

type Wrapper = <Args extends unknown[], Return>(
  fn: (todistusType: CertificateTypes, ...rest: Args) => Return
) => (...args: Args) => Return

export default (
  todistusType: VtjOteTodistusType
): {
  applyDefaultLanguage: Wrapped<typeof applyDefaultLanguage>
  setPreviousAddressesOverYears: Wrapped<typeof setPreviousAddressesOverYears>
  setTodistusLanguage: Wrapped<typeof setTodistusLanguage>
  changeCertificationLanguage: Wrapped<typeof changeCertificationLanguage>
  setTranslationFormLanguage: Wrapped<typeof setTranslationFormLanguage>
  setUsageRegion: Wrapped<typeof setUsageRegion>
  submitForm: Wrapped<typeof submitForm>
  toggleIncludePreviousNames: Wrapped<typeof toggleIncludePreviousNames>
  toggleIncludeSpousePreviousNames: Wrapped<
    typeof toggleIncludeSpousePreviousNames
  >
  toggleIncludeHetu: Wrapped<typeof toggleIncludeHetu>
  toggleIncludeMaritalStatus: Wrapped<typeof toggleIncludeMaritalStatus>
  toggleIncludePreviousAddresses: Wrapped<typeof toggleIncludePreviousAddresses>
  toggleIncludePreviousMarriages: Wrapped<typeof toggleIncludePreviousMarriages>
  toggleIncludeTranslationForm: Wrapped<typeof toggleIncludeTranslationForm>
  toggleSubject: Wrapped<typeof toggleSubject>
  toggleIncludeApostille: Wrapped<typeof toggleIncludeApostille>
  ordererExtraIdentification: {
    setBirthPlace: Wrapped<typeof setOrdererExtraIdentificationBirthPlace>
    setBirthCountry: Wrapped<typeof setOrdererExtraIdentificationBirthCountry>
    setFinnishHetu: Wrapped<typeof setOrdererExtraIdentificationFinnishHetu>
    setAddForeignPersonId: Wrapped<
      typeof setOrdererExtraIdentificationAddForeignPersonId
    >
    setForeignPersonId: Wrapped<
      typeof setOrdererExtraIdentificationForeignPersonId
    >
    setForeignPersonIdCountry: Wrapped<
      typeof setOrdererExtraIdentificationForeignPersonIdCountry
    >
    setAddPreviousName: Wrapped<
      typeof setOrdererExtraIdentificationAddPreviousName
    >
    setPreviousName: Wrapped<typeof setOrdererExtraIdentificationPreviousName>
  }
  mailDelivery: {
    setFirstName: Wrapped<typeof setMailDeliveryFirstName>
    setLastName: Wrapped<typeof setMailDeliveryLastName>
    setStreetAddress: Wrapped<typeof setMailDeliveryStreetAddress>
    setPostOffice: Wrapped<typeof setMailDeliveryPostOffice>
    setPostalCode: Wrapped<typeof setMailDeliveryPostalCode>
    setCountry: Wrapped<typeof setMailDeliveryCountry>
    setStreetAddressExtras: Wrapped<typeof setMailDeliveryStreetAddressExtras>
  }
  contactInfo: {
    setEmail: Wrapped<typeof setContactInfoEmail>
    setPhoneNumber: Wrapped<typeof setContactInfoPhoneNumber>
  }
} => {
  const wrap: Wrapper = (fn) => {
    return (...args) => {
      if (!isValueInEnum(todistusType, OrderFlowName)) {
        throw new Error(
          'Type check failed for "todistusType". Only VtjOtetodistusTypes are supported here'
        )
      }
      clearVisitsAfterCurrentStep(todistusType)
      return fn(todistusType, ...args)
    }
  }

  return {
    applyDefaultLanguage: wrap(applyDefaultLanguage),
    setPreviousAddressesOverYears: wrap(setPreviousAddressesOverYears),
    setTodistusLanguage: wrap(setTodistusLanguage),
    changeCertificationLanguage: wrap(changeCertificationLanguage),
    setTranslationFormLanguage: wrap(setTranslationFormLanguage),
    setUsageRegion: wrap(setUsageRegion),
    submitForm: wrap(submitForm),
    toggleIncludePreviousNames: wrap(toggleIncludePreviousNames),
    toggleIncludeSpousePreviousNames: wrap(toggleIncludeSpousePreviousNames),
    toggleIncludeHetu: wrap(toggleIncludeHetu),
    toggleIncludeMaritalStatus: wrap(toggleIncludeMaritalStatus),
    toggleIncludePreviousAddresses: wrap(toggleIncludePreviousAddresses),
    toggleIncludePreviousMarriages: wrap(toggleIncludePreviousMarriages),
    toggleIncludeTranslationForm: wrap(toggleIncludeTranslationForm),
    toggleSubject: wrap(toggleSubject),
    toggleIncludeApostille: wrap(toggleIncludeApostille),
    ordererExtraIdentification: {
      setBirthPlace: wrap(setOrdererExtraIdentificationBirthPlace),
      setBirthCountry: wrap(setOrdererExtraIdentificationBirthCountry),
      setFinnishHetu: wrap(setOrdererExtraIdentificationFinnishHetu),
      setAddForeignPersonId: wrap(
        setOrdererExtraIdentificationAddForeignPersonId
      ),
      setForeignPersonId: wrap(setOrdererExtraIdentificationForeignPersonId),
      setForeignPersonIdCountry: wrap(
        setOrdererExtraIdentificationForeignPersonIdCountry
      ),
      setAddPreviousName: wrap(setOrdererExtraIdentificationAddPreviousName),
      setPreviousName: wrap(setOrdererExtraIdentificationPreviousName),
    },
    mailDelivery: {
      setFirstName: wrap(setMailDeliveryFirstName),
      setLastName: wrap(setMailDeliveryLastName),
      setStreetAddress: wrap(setMailDeliveryStreetAddress),
      setPostOffice: wrap(setMailDeliveryPostOffice),
      setPostalCode: wrap(setMailDeliveryPostalCode),
      setCountry: wrap(setMailDeliveryCountry),
      setStreetAddressExtras: wrap(setMailDeliveryStreetAddressExtras),
    },
    contactInfo: {
      setEmail: wrap(setContactInfoEmail),
      setPhoneNumber: wrap(setContactInfoPhoneNumber),
    },
  }
}

const setContactInfoEmail = (
  todistusType: CertificateTypes,
  value: TextInputValue
) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    const form = forms[todistusType]
    form.contactInfo.email = editIlmoitettuArvo(
      form.contactInfo.email,
      value === undefined ? '' : String(value)
    )
  })
}

const setContactInfoPhoneNumber = (
  todistusType: CertificateTypes,
  value: TextInputValue
) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    const form = forms[todistusType]
    form.contactInfo.phoneNumber = editIlmoitettuArvo(
      form.contactInfo.phoneNumber,
      value === undefined ? '' : String(value)
    )
  })
}

const createMailDeliverySetter = (
  propertyName: keyof TodistusStoreForm['mailDelivery']
): ((
  todistusType: CertificateTypes,
  value: string | number | undefined
) => void) => {
  return (todistusType, value) => {
    void createActionWithRefreshSession(
      ({ forms: { [todistusType]: form } }: TodistuksetUi.Store) => {
        form.mailDelivery[propertyName] = editIlmoitettuArvo(
          form.mailDelivery[propertyName],
          value === undefined ? '' : String(value)
        )
      }
    )
  }
}

const setMailDeliveryFirstName = createMailDeliverySetter('firstName')
const setMailDeliveryLastName = createMailDeliverySetter('lastName')
const setMailDeliveryStreetAddress = createMailDeliverySetter('streetAddress')
const setMailDeliveryPostOffice = createMailDeliverySetter('postOffice')
const setMailDeliveryPostalCode = createMailDeliverySetter('postalCode')
const setMailDeliveryCountry = createMailDeliverySetter('country')
const setMailDeliveryStreetAddressExtras = createMailDeliverySetter(
  'streetAddressExtras'
)

const createOptionalSetter = <T extends keyof TodistusStoreForm>(
  propertyName: T
) => {
  return (todistusType: CertificateTypes, value: TodistusStoreForm[T]) => {
    void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
      forms[todistusType][propertyName] = value
    })
  }
}

const setTodistusLanguage = createOptionalSetter('todistusLanguage')
const setTranslationFormLanguage = createOptionalSetter(
  'translationFormLanguage'
)

const changeCertificationLanguage = (
  todistusType: CertificateTypes,
  languageSelection: LanguageCode
) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    forms[todistusType].todistusLanguage = editIlmoitettuArvo<TodistusKieli>(
      null,
      languageSelection as LanguageCode.FI | LanguageCode.SV | LanguageCode.EN
    )
    if (
      forms[todistusType].usageRegion.countryKoodistoCode ===
        EuCountries.FINLAND ||
      forms[todistusType].usageRegion.countryKoodistoCode === EuCountries.SWEDEN
    ) {
      forms[todistusType].includeTranslationForm =
        editIlmoitettuArvo<IncludeKaannos>(null, false)
    } else if (languageSelection === LanguageCode.EN) {
      forms[todistusType].includeTranslationForm =
        editIlmoitettuArvo<IncludeKaannos>(null, false)
      forms[todistusType].translationFormLanguage =
        editIlmoitettuArvo<KaannosKieli>(null, null)
    } else {
      forms[todistusType].includeTranslationForm =
        editIlmoitettuArvo<IncludeKaannos>(
          null,
          finalEditableFieldValue(
            forms[todistusType].translationFormLanguage
          ) !== null
        )
    }
  })
}

type EditableBooleanKeys<T> = {
  [K in keyof T]: T[K] extends EditableField<boolean> ? K : never
}[keyof T]
type NoUndefined<T> = T extends undefined ? never : T
type EditableBooleanKeysOfForm = NoUndefined<
  EditableBooleanKeys<TodistusStoreForm>
>

const createEditableBooleanToggler = (
  propertyName: EditableBooleanKeysOfForm
) => {
  return (todistusType: CertificateTypes) => {
    void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
      const currentValue = forms[todistusType][propertyName]
      forms[todistusType][propertyName] = editIlmoitettuArvo(
        currentValue,
        !currentValue.ilmoitettuArvo
      )
    })
  }
}

const toggleIncludePreviousNames = createEditableBooleanToggler(
  'includePreviousNames'
)
const toggleIncludeSpousePreviousNames = createEditableBooleanToggler(
  'includeSpousePreviousNames'
)
const toggleIncludeHetu = createEditableBooleanToggler('includeHetu')
const toggleIncludeMaritalStatus = createEditableBooleanToggler(
  'includeMaritalStatus'
)
const toggleIncludePreviousMarriages = createEditableBooleanToggler(
  'includePreviousMarriages'
)
const toggleIncludePreviousAddresses = (todistusType: CertificateTypes) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    const currentValue = forms[todistusType].includePreviousPermanentAddresses
    forms[todistusType].includePreviousPermanentAddresses =
      editIlmoitettuArvo<IncludePreviousPermanentAddresses>(
        null,
        !currentValue.ilmoitettuArvo
      )
    forms[todistusType].includePreviousTemporaryAddresses =
      editIlmoitettuArvo<IncludePreviousTemporaryAddresses>(
        null,
        !currentValue.ilmoitettuArvo
      )
    forms[todistusType].includePreviousKotikunta =
      editIlmoitettuArvo<IncludePreviousKotikunta>(
        null,
        !currentValue.ilmoitettuArvo
      )
  })
}

const toggleIncludeTranslationForm = (todistusType: CertificateTypes) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    const currentValue = forms[todistusType]['includeTranslationForm']
    forms[todistusType]['includeTranslationForm'] = editIlmoitettuArvo(
      currentValue,
      !currentValue.ilmoitettuArvo
    )
    if (currentValue.ilmoitettuArvo) {
      forms[todistusType]['translationFormLanguage'] = emptyEditableField()
    }
    forms[todistusType]['includeApostille'] =
      editIlmoitettuArvo<IncludeApostille>(null, false)
    forms[todistusType]['todistusSubtype'] =
      CertificateSubtypes.EU_VIRANOMAISELLE
  })
}

const toggleIncludeApostille = (todistusType: CertificateTypes) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    const currentValue = forms[todistusType]['includeApostille']
    forms[todistusType]['includeApostille'] =
      editIlmoitettuArvo<IncludeApostille>(
        currentValue,
        !currentValue.ilmoitettuArvo
      )
    forms[todistusType]['includeTranslationForm'] =
      editIlmoitettuArvo<IncludeKaannos>(null, false)
    forms[todistusType]['translationFormLanguage'] =
      editIlmoitettuArvo<KaannosKieli>(null, null)
    if (currentValue.ilmoitettuArvo === false) {
      forms[todistusType]['todistusSubtype'] = CertificateSubtypes.APOSTILLE
    } else {
      forms[todistusType]['todistusSubtype'] =
        CertificateSubtypes.EU_VIRANOMAISELLE
    }
  })
}

const applyDefaultLanguage = (todistusType: CertificateTypes) => {
  void createActionWithRefreshSession(
    ({ forms, common }: TodistuksetUi.Store) => {
      if (forms[todistusType].todistusLanguage === null) {
        forms[todistusType].todistusLanguage =
          editIlmoitettuArvo<TodistusKieli>(
            null,
            common.lang as LanguageCode.FI | LanguageCode.SV | LanguageCode.EN
          )
      }
    }
  )
}

const setPreviousAddressesOverYears = (
  todistusType: CertificateTypes,
  value: TextInputValue
) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    const asInt = parseInt(value === undefined ? '' : String(value), 10)
    const isValid = !isNaN(asInt) && asInt > 0
    forms[todistusType].previousAddressesOverYears =
      editIlmoitettuArvo<PreviousAddressesOverYears>(
        null,
        isValid ? Math.min(asInt, MAX_PREVIOUS_ADDRESSES_OVER_YEARS) : null
      )
  })
}

const setUsageRegion = (
  todistusType: CertificateTypes,
  value: TodistusStoreForm['usageRegion']
) => {
  void createActionWithRefreshSession(
    ({ forms, common }: TodistuksetUi.Store) => {
      const form = forms[todistusType]

      if (value.target === UsageRegionTarget.EU_AUTHORITY) {
        // User selects "EU AUTHORITY"
        // Need to reset possibly selected usage region props
        // because form submit does not work otherwise
        form.usageRegion = {
          // EU AUTHORITY has mandatory FINLAND in country
          countryKoodistoCode: EuCountries.FINLAND,
          target: UsageRegionTarget.EU_AUTHORITY,
          receiver: null,
        }
      } else {
        // User has selected "TARGET COUNTRY" just pass usageRegion to the state
        form.usageRegion = value
      }

      // Set subtype explicitly in every case to avoid
      // invalid / conflicting order forms
      const isTargetEuAuthority =
        form.usageRegion.target === UsageRegionTarget.EU_AUTHORITY
      const isTargetFinland =
        form.usageRegion.target === UsageRegionTarget.TARGET_COUNTRY &&
        form.usageRegion.countryKoodistoCode === EuCountries.FINLAND

      if (isTargetEuAuthority || isTargetFinland) {
        if (isEidasOrderer(form.orderer)) {
          form.todistusSubtype = CertificateSubtypes.EU_VIRANOMAISELLE
        } else {
          form.todistusSubtype = CertificateSubtypes.SAHKOINEN
        }
      } else {
        form.todistusSubtype = CertificateSubtypes.EU_VIRANOMAISELLE
      }

      // Receiver is set only for eu-countries (except Finland)
      // so we need reset it in other cases
      if (
        !isCountryInEUByCode(form.usageRegion.countryKoodistoCode) ||
        form.usageRegion.countryKoodistoCode === EuCountries.FINLAND
      ) {
        form.usageRegion.receiver = null
      }

      form.includeApostille.ilmoitettuArvo = false

      const usageRegionIsSweden =
        form.usageRegion.countryKoodistoCode === EuCountries.SWEDEN
      form.todistusLanguage = editIlmoitettuArvo<TodistusKieli>(
        null,
        usageRegionIsSweden
          ? LanguageCode.SV
          : (common.lang as LanguageCode.FI | LanguageCode.SV | LanguageCode.EN)
      )
      form.includeTranslationForm = editIlmoitettuArvo<IncludeKaannos>(
        null,
        checkIfTranslationIsAvailable({
          usageRegion: value,
          todistusLanguage: finalEditableFieldValue(form.todistusLanguage),
        })
      )
      form.translationFormLanguage = emptyEditableField()
    }
  )
}

const toggleSubject = (
  todistusType: CertificateTypes,
  subject: TodistuksetUser | Huollettava
) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    const { persons } = forms[todistusType]
    const person = persons.find((person) =>
      isSameHetuOrEidasPID(subject, person)
    )
    if (!person) {
      persons.push(huollettavaToPerson(subject as Huollettava))
    } else if (!person.roles.includes(TodistusPersonRole.TILAAJA)) {
      forms[todistusType].persons = persons.filter(
        (person) => !isSameHetuOrEidasPID(subject, person)
      )
    }
    // Tilaaja will always be one of the persons, so we need different handling
    else if (person.roles.includes(TodistusPersonRole.KOHDEHENKILO)) {
      person.roles = person.roles.filter(
        (role) => role !== TodistusPersonRole.KOHDEHENKILO
      )
    } else {
      person.roles = [...person.roles, TodistusPersonRole.KOHDEHENKILO]
    }
  })
}

const getOrdererExtraIdentificationSetter = <
  Key extends keyof OrdererExtraIdentification
>(
  fieldName: Key,
  { emptyValue }: { emptyValue: OrdererExtraIdentification[Key] }
) => {
  return (
    todistusType: CertificateTypes,
    value: OrdererExtraIdentification[Key] | number | undefined
  ): void => {
    // We don't use input types that would result in a number
    if (typeof value === 'number') {
      throw new Error(`${fieldName} must not be a number`)
    }
    void createActionWithRefreshSession(
      ({ forms: { [todistusType]: form } }: TodistuksetUi.Store) => {
        if (form.ordererExtraIdentification) {
          form.ordererExtraIdentification[fieldName] = value || emptyValue
        }
      }
    )
  }
}

const setOrdererExtraIdentificationBirthPlace =
  getOrdererExtraIdentificationSetter('birthPlace', {
    emptyValue: '',
  })
const setOrdererExtraIdentificationBirthCountry =
  getOrdererExtraIdentificationSetter('birthCountry', {
    emptyValue: '',
  })
const setOrdererExtraIdentificationFinnishHetu =
  getOrdererExtraIdentificationSetter('finnishHetu', {
    emptyValue: null,
  })
const setOrdererExtraIdentificationForeignPersonId =
  getOrdererExtraIdentificationSetter('foreignPersonId', { emptyValue: null })
const setOrdererExtraIdentificationForeignPersonIdCountry =
  getOrdererExtraIdentificationSetter('foreignPersonIdCountry', {
    emptyValue: null,
  })
const setOrdererExtraIdentificationPreviousName =
  getOrdererExtraIdentificationSetter('previousName', {
    emptyValue: null,
  })

const setOrdererExtraIdentificationAddForeignPersonId = (
  todistusType: CertificateTypes,
  value: boolean
) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    forms[todistusType].ordererExtraIdentificationOptions.addForeignPersonId =
      value
    const { ordererExtraIdentification } = forms[todistusType]
    if (!value && ordererExtraIdentification) {
      ordererExtraIdentification.foreignPersonId = null
      ordererExtraIdentification.foreignPersonIdCountry = null
    }
  })
}

const setOrdererExtraIdentificationAddPreviousName = (
  todistusType: CertificateTypes,
  value: boolean
) => {
  void createActionWithRefreshSession(({ forms }: TodistuksetUi.Store) => {
    forms[todistusType].ordererExtraIdentificationOptions.addPreviousName =
      value
    const { ordererExtraIdentification } = forms[todistusType]
    if (!value && ordererExtraIdentification) {
      ordererExtraIdentification.previousName = null
    }
  })
}

export type CreateOrderV2Response = {
  paytrailHref: string
  paymentId: number
  isCertificateFreeOfCharge: boolean
  orderCompleteHref: string
}

const submitForm = (todistusType: CertificateTypes) =>
  createFlow(function* (
    store: TodistuksetUi.Store
  ): Generator<
    ReturnType<typeof post>,
    void,
    HttpResult<CreateOrderV2Response>
  > {
    if (store.order.requestStatus === RequestStatus.SENDING) {
      return
    }
    const form = toJS(store.forms[todistusType])
    const sanitizedForm: Omit<
      TodistusStoreForm,
      'ordererExtraIdentificationOptions'
    > = {
      ...fp.omit(['ordererExtraIdentificationOptions'], form),
      mailDelivery: fp.mapValues(
        (field) =>
          mapEditableField(field, (value) =>
            value !== null ? sanitizeMailDeliveryInput(value) : null
          ),
        form.mailDelivery
      ) as MailDelivery,
      contactInfo: {
        ...form.contactInfo,
        phoneNumber: {
          vtjArvo: null,
          ilmoitettuArvo: form.contactInfo.phoneNumber?.ilmoitettuArvo
            ? removeSpaces(form.contactInfo.phoneNumber.ilmoitettuArvo)
            : null,
          virkailijaArvo: form.contactInfo.phoneNumber?.virkailijaArvo
            ? removeSpaces(form.contactInfo.phoneNumber.virkailijaArvo)
            : null,
        },
      },
      persons: form.persons,
      ordererExtraIdentification: form.ordererExtraIdentification
        ? (fp.mapValues((v: string | null) => {
            const normalized = normalizeTextInputWhitespace(v)
            return normalized || null
          }, form.ordererExtraIdentification) as unknown as OrdererExtraIdentification)
        : undefined,
    }
    if (finalEditableFieldValue(form.includeApostille)) {
      sanitizedForm.todistusSubtype = CertificateSubtypes.APOSTILLE
    }

    store.order.requestStatus = RequestStatus.SENDING

    const storePath = 'order.requestStatus'
    const errorValue = RequestStatus.ERROR
    try {
      const [error, response] = yield post(
        `/api/restricted/todistukset/create-order-v2`,
        {
          form: sanitizedForm,
        }
      )
      if (error) {
        throw error
      }

      store.order.requestStatus = RequestStatus.SENT

      response.data.isCertificateFreeOfCharge
        ? window.location.replace(response.data.orderCompleteHref)
        : window.location.replace(response.data.paytrailHref)
    } catch (error) {
      handleError({ error, storePath, errorValue })
    }
  })

export const updateFormPersonsAsiointikieli = (newLang: LanguageCode): void =>
  createAction((store: TodistuksetUi.Store) => {
    const { forms, aetOrdererForm, aetSignerForms, uaetForm } = store
    const asiointikieli = langIsTodistusasiointikieli(newLang)
      ? newLang
      : LanguageCode.FI

    if (forms) {
      Object.keys(forms).forEach((key) => {
        const form = forms[key as keyof typeof forms]
        const ordererPerson = form.persons.find((person) =>
          person.roles.includes(TodistusPersonRole.TILAAJA)
        )
        if (ordererPerson) {
          ordererPerson.asiointikieli = asiointikieli
        }
      })
    }

    Object.keys(aetSignerForms).forEach((key) => {
      const form = aetSignerForms[Number(key)]
      form.orderer.asiointikieli = asiointikieli
    })
    aetOrdererForm.orderer.asiointikieli = asiointikieli
    uaetForm.spouseA.asiointikieli = asiointikieli
  })
